import { Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConnectionStatus, Network } from '@capacitor/network';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { ExamDojoFeatureFlag } from '@examdojo/models/feature-flag';
import { PlatformService } from '@examdojo/platform';
import { shareOneReplay } from '@examdojo/rxjs';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  of,
  pairwise,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ExamdojoPlatformService extends PlatformService implements OnDestroy {
  constructor(private readonly featureFlagService: FeatureFlagService) {
    super();
    this.listenForNetworkStatus().pipe(takeUntilDestroyed()).subscribe();
  }

  private readonly networkStatus$ = new BehaviorSubject<ConnectionStatus | null>(null);

  readonly activeStatusState$ = this.featureFlagService.select(ExamDojoFeatureFlag.TestingLock).pipe(
    switchMap((enabled) => {
      if (!enabled) {
        return of(true);
      }

      return combineLatest([
        this.networkStatus$,
        fromEvent(document, 'visibilitychange').pipe(
          map(() => document.visibilityState === 'visible'),
          startWith(document.visibilityState === 'visible'),
        ),
      ]).pipe(
        map(([networkStatus, isVisible]) => !!networkStatus?.connected && isVisible),
        distinctUntilChanged(),
      );
    }),
    shareOneReplay(),
  );

  readonly trigger$ = this.featureFlagService.select(ExamDojoFeatureFlag.TestingLock).pipe(
    filter(Boolean),
    switchMap(() => {
      return merge(
        this.networkStatus$.pipe(
          pairwise(),
          filter(([prev, curr]) => !!prev && !!curr && !prev.connected && curr.connected),
        ),
        this.pageVisible$.pipe(
          withLatestFrom(this.networkStatus$),
          filter(([, networkStatus]) => !!networkStatus?.connected),
          map(([event]) => event),
        ),
      ).pipe(debounceTime(100));
    }),
  );

  ngOnDestroy() {
    Network.removeAllListeners();
  }

  private listenForNetworkStatus() {
    return this.featureFlagService.select(ExamDojoFeatureFlag.TestingLock).pipe(
      filter(Boolean),
      tap(async () => {
        this.networkStatus$.next(await Network.getStatus());

        Network.addListener('networkStatusChange', (status) => {
          this.networkStatus$.next(status);
        });
      }),
    );
  }
}
